import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { UserLanguage } from '../models/user.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  private _currentLang = signal<UserLanguage>('fr');
  private translations = signal<Record<string, string>>({});
  private cache = new Map<string, Record<string, string>>();

  async init(): Promise<void> {
    const lang = (this.auth.currentUser()?.language ?? 'fr') as UserLanguage;
    await this.loadTranslations(lang);
    this._currentLang.set(lang);
    this.translations.set(this.cache.get(lang)!);
  }

  t(key: string): string {
    return this.translations()[key] ?? key;
  }

  locale(): string {
    const stored = this.auth.currentUser()?.locale;
    if (stored) return stored;
    const map: Record<UserLanguage, string> = { fr: 'fr-FR', de: 'de-DE', en: 'en-US' };
    return map[this._currentLang()] ?? Intl.DateTimeFormat().resolvedOptions().locale;
  }

  timezone(): string {
    return this.auth.currentUser()?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString(this.locale(), {
      hour: '2-digit', minute: '2-digit',
      timeZone: this.timezone(),
    });
  }

  isoToTzHHMM(iso: string): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: this.timezone(),
      hour: '2-digit', minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(iso));
    return `${parts.find(p => p.type === 'hour')!.value}:${parts.find(p => p.type === 'minute')!.value}`;
  }

  isoToTzDate(iso: string): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: this.timezone(),
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date(iso));
  }

  tzDateHHMMToISO(dateStr: string, hhmm: string): string {
    const [h, m] = hhmm.split(':').map(Number);
    const tz = this.timezone();
    const localStr = `${dateStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
    const asUTC = new Date(localStr + 'Z');
    const corrParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(asUTC);
    const f = (type: string) => corrParts.find(p => p.type === type)!.value;
    const tzAsUTC = new Date(`${f('year')}-${f('month')}-${f('day')}T${f('hour')}:${f('minute')}:${f('second')}Z`);
    return new Date(asUTC.getTime() + (asUTC.getTime() - tzAsUTC.getTime())).toISOString();
  }

  tzHHMMToISO(baseDateIso: string, hhmm: string): string {
    return this.tzDateHHMMToISO(this.isoToTzDate(baseDateIso), hhmm);
  }

  async setLanguage(lang: UserLanguage): Promise<void> {
    await this.applyLanguage(lang);
    const user = this.auth.currentUser();
    if (user) {
      this.auth.currentUser.set({ ...user, language: lang });
    }
    try {
      await firstValueFrom(
        this.http.put(`${environment.apiUrl}/users/me`, { language: lang })
      );
    } catch {
      // offline — will sync later
    }
  }

  async applyLanguage(lang: UserLanguage): Promise<void> {
    await this.loadTranslations(lang);
    this._currentLang.set(lang);
    this.translations.set(this.cache.get(lang)!);
  }

  private async loadTranslations(lang: string): Promise<void> {
    if (this.cache.has(lang)) return;
    const data = await firstValueFrom(
      this.http.get<Record<string, string>>(`/assets/i18n/${lang}.json`)
    );
    this.cache.set(lang, data);
  }
}
